import { forwardRef, useCallback, useState } from 'react';

import { Alert, Box, Paragraph } from '@zillow/constellation-10';

import { getStringConfig } from '@zg-rentals/frontend-config/client';

import { ListingActivationTerms } from '@zg-rentals/zillow-rental-manager';
import AlicornPaymentIframe from '../AlicornPaymentIframe';
import { PaymentMethodIframeSkeleton } from '../AlicornPaymentIframe/PaymentMethodIframeSkeleton';

import type { ReactNode } from 'react';
import type { FreemiumStartPaymentMethodSessionV2ResponseWebDto } from '@zg-rentals/ts-rental-manager-api-client';
import type { MetroidCaptureError } from '../AlicornPaymentIframe';

export type Props = {
  sessionData?: FreemiumStartPaymentMethodSessionV2ResponseWebDto;
  formErrorMessage: string;
  isFormLoaded: boolean;
  isCaptured: boolean;
  onRenderComplete: () => void;
  onSubmitSuccess: () => void;
  onSubmitFailure: (error: MetroidCaptureError) => void;
  useFreemiumValues?: boolean;
  displayedInSelectPaymentMethodModal?: boolean;
  pricingContent?: ReactNode;
};

const CreditCardCapture = forwardRef<HTMLDivElement, Props>(
  (
    {
      sessionData,
      formErrorMessage,
      isFormLoaded,
      isCaptured,
      onRenderComplete,
      onSubmitSuccess,
      onSubmitFailure,
      useFreemiumValues = false,
      displayedInSelectPaymentMethodModal = false,
      pricingContent,
    },
    ref,
  ) => {
    const [showLoading, setShowLoading] = useState(!isFormLoaded || isCaptured);

    const iframeButtonText = getStringConfig('metroidIframeButtonText', 'Submit');

    const onRenderStart = useCallback(() => setShowLoading(true), []);

    const onRenderFinish = useCallback(() => {
      setShowLoading(false);
      onRenderComplete();
    }, [onRenderComplete]);

    return (
      <Box
        ref={ref}
        css={{
          display: 'flex',
          flexDirection: 'column',
          '& #z_hppm_iframe': {
            backgroundColor: 'transparent',
            width: '100%',
          },
        }}
      >
        <Box
          css={{
            maxWidth: '100%',
            margin: '0 auto',
            padding: '200',
          }}
        >
          {!showLoading && !displayedInSelectPaymentMethodModal && pricingContent}
          {displayedInSelectPaymentMethodModal && (
            <Alert
              tone="warning"
              css={{ marginTop: '200', marginBottom: '600' }}
              data-testid="select-payment-method-alert"
            >
              This card will become the default payment method for all future listing premium upgrades
            </Alert>
          )}
          {showLoading && <PaymentMethodIframeSkeleton />}
          <AlicornPaymentIframe
            sessionData={sessionData}
            onRenderStart={onRenderStart}
            onRenderComplete={onRenderFinish}
            onSubmitSuccess={onSubmitSuccess}
            onSubmitFailure={onSubmitFailure}
          />
          {!showLoading && formErrorMessage && (
            <Paragraph data-testid="error-text" css={{ marginBottom: '400', color: 'text.feedback.critical.hero' }}>
              {formErrorMessage}
            </Paragraph>
          )}
          <ListingActivationTerms
            actionText={`By clicking ${iframeButtonText}`}
            useFreemiumValues={useFreemiumValues}
            isLoading={showLoading}
          />
        </Box>
      </Box>
    );
  },
);

CreditCardCapture.displayName = 'CreditCardCapture';

export default CreditCardCapture;
